import { rpx } from 'client/lib/rpx-client';
import { AuthForm, CheckoutForm } from '@course/components/checkout';
import { intl } from 'shared/intl/use-intl';
import { useAuth, useCurrentTenant, useCurrentUser } from 'client/lib/auth';
import { setUrlSearchParam } from 'client/utils/url';
import {
  CourseCheckoutPage,
  CourseCheckoutErrorPage,
  redirectToCourse,
} from '@course/components/course-checkout';
import { CourseBanner } from '@course/components/course-checkout/course-banner';
import { SiteNavLogo } from '@course/components/nav/site-nav-logo';
import { MiraseeLogo } from '@course/components/mirasee-logo';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { defStudentRoute } from '@course/components/courses-app-router';
import { URLS } from 'shared/urls';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { BtnPrimary, Button } from '@components/buttons';
import { showError } from '@components/app-error';
import { SetPasswordForm } from '@course/components/checkout/set-password-form';
import { FormGroup, InputLabel } from '@components/async-form';
import { Password } from '@components/password';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { UserProfileIcon } from '@components/avatars/user-profile-icon';
import { BioEditor } from '@course/components/bio-editor';
import { filepicker } from 'client/components/filepicker';
import { useImageUrl } from 'client/utils/cdn';

export const route = defStudentRoute({
  isPublic: true,
  load,
  Page,
});

async function load(route: RouteLoadProps) {
  const { courseId } = route.params;
  const invitationString = route.params.invitation;
  const result = await rpx.invitations.getInvitationState({
    courseId,
    invitation: invitationString,
  });

  if (result.isMember) {
    await redirectToCourse(route.router, result.course);
  }

  return {
    ...result,
    invitationString,
    course: {
      ...result.course,
      subcourses: result.subcourses,
    },
  };
}

type Props = LoadedProps<typeof load>;

function getRedirectUrl(course: { id: string; title: string }) {
  if (course.id === AIP_COURSE_ID) {
    return '/community';
  }
  return URLS.student.course({ course });
}

function Page(props: Props) {
  const { course, isFull, invitation, invitationString, recipientName } = props.state;
  const isBulk = !!recipientName;

  if (invitation.userId) {
    const courseUrl = URLS.student.course({ course });
    return (
      <CourseCheckoutErrorPage course={course} title={intl('Invitation used')}>
        <span>
          {intl('This invitation has already been accepted.')}{' '}
          <a href={courseUrl} class="text-blue-600 hover:text-blue-800 underline">
            {intl('Click here to access the course.')}
          </a>
        </span>
      </CourseCheckoutErrorPage>
    );
  }

  if (isFull) {
    return (
      <CourseCheckoutErrorPage course={course} title={intl('Course full')}>
        {intl('This course is full.')}
      </CourseCheckoutErrorPage>
    );
  }

  if (isBulk) {
    return (
      <BulkInvitationFlow
        props={props}
        recipientName={recipientName}
        course={course}
        invitation={invitation}
        invitationString={invitationString}
      />
    );
  }

  return <LegacyInvitationFlow props={props} />;
}

function BulkInvitationFlow({
  props,
  recipientName,
  course,
  invitation,
  invitationString,
}: {
  props: Props;
  recipientName: string;
  course: Props['state']['course'];
  invitation: Props['state']['invitation'];
  invitationString: string;
}) {
  const auth = useAuth();
  const flowKind = props.state.flowKind;
  const isTrialFlow = flowKind === 'trial_with_card';
  const currentUser = useCurrentUser();
  const [mode, setMode] = useState<
    'welcome' | 'login' | 'profile' | 'accepted' | 'consent' | 'collect_card' | 'no_account'
  >(() => {
    if (isTrialFlow) {
      if (!invitation.existingUser) {
        return 'no_account';
      }
      return currentUser ? 'consent' : 'login';
    }
    return 'welcome';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [acceptError, setAcceptError] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const redirectUrl = getRedirectUrl(course);
  const firstName = recipientName.trim().split(' ')[0] || recipientName.trim() || 'there';

  const handleAccept = async () => {
    try {
      setIsLoading(true);
      setAcceptError(false);
      if (invitation.existingUser) {
        setMode('login');
        return;
      }
      const user = await rpx.invitations.acceptBulkInvitation({
        courseId: course.id,
        invitation: invitationString,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await auth.login(Promise.resolve({ ...user, level: 'student' }));
      setMode('profile');
    } catch {
      setAcceptError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTrialClaim = async (paymentMethodId?: string) => {
    try {
      setIsLoading(true);
      setAcceptError(false);
      const result = await rpx.invitations.acceptInvitationWithTrial({
        courseId: course.id,
        invitation: invitationString,
        consentAcknowledged: true as const,
        paymentMethodId,
      });
      if (result.status === 'requiresCard') {
        setMode('collect_card');
        return;
      }
      await props.router.redirectTo(redirectUrl);
    } catch (err) {
      showError(err);
      setAcceptError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (password: string) => {
    try {
      setIsLoading(true);
      const user = await rpx.auth.login({ email: invitation.email, password });
      await auth.login(Promise.resolve({ ...user, level: 'student' }));
      if (isTrialFlow) {
        setMode('consent');
        return;
      }
      await rpx.invitations.acceptInvitation({
        courseId: course.id,
        invitation: invitationString,
      });
      await props.router.redirectTo(redirectUrl);
    } catch (err) {
      showError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useDocumentTitle([course.title]);

  if (mode === 'no_account') {
    return (
      <PageWrapper>
        <CourseCheckoutErrorPage course={course} title={intl(`We couldn't find your account`)}>
          <span>
            {intl(
              `It looks like the email on your invitation doesn't match the one you used for AI Time Machine. Please reach out to support@mirasee.com and we'll get you sorted.`,
            )}
          </span>
        </CourseCheckoutErrorPage>
      </PageWrapper>
    );
  }

  if (mode === 'consent') {
    return (
      <PageWrapper>
        <TrialConsent
          firstName={firstName}
          isLoading={isLoading}
          acceptError={acceptError}
          onAccept={() => handleTrialClaim()}
        />
      </PageWrapper>
    );
  }

  if (mode === 'collect_card') {
    return (
      <PageWrapper>
        <div class="flex flex-col gap-4">
          <h3 class="text-lg font-bold">{intl(`We couldn't find a saved card`)}</h3>
          <p class="text-gray-600">
            {intl(
              `We weren't able to find the card you used for AI Time Machine. Please reach out to support@mirasee.com and we'll get you set up.`,
            )}
          </p>
        </div>
      </PageWrapper>
    );
  }

  if (mode === 'profile') {
    return (
      <PageWrapper>
        <ProfileStep onContinue={() => setMode('accepted')} onSkip={() => setMode('accepted')} />
      </PageWrapper>
    );
  }

  if (mode === 'accepted') {
    return (
      <PageWrapper>
        <SetPasswordForm
          title={intl("You're in!")}
          subtitle="Set a password so you can easily log in later. You can also log in anytime by having a link sent to your email."
          onComplete={async () => {
            await props.router.redirectTo(redirectUrl);
          }}
          onSkip={async () => {
            await props.router.redirectTo(redirectUrl);
          }}
        />
      </PageWrapper>
    );
  }

  if (mode === 'login') {
    return (
      <PageWrapper>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <h3 class="text-lg font-bold">{`Welcome back, ${firstName}!`}</h3>
            <p class="text-gray-600">Enter your password to accept this invitation.</p>
          </div>
          <form
            class="flex flex-col gap-4"
            onSubmit={async (e: Event) => {
              e.preventDefault();
              const data = new FormData(e.target as HTMLFormElement);
              await handleLogin(data.get('password') as string);
            }}
          >
            <FormGroup prop="password">
              <InputLabel>{intl('Password')}</InputLabel>
              <Password
                name="password"
                placeholder={intl('Enter your password')}
                autocomplete="current-password"
                autoFocus
                class="w-full"
              />
            </FormGroup>
            <BtnPrimary class="w-full py-3 rounded-md font-semibold" isLoading={isLoading}>
              {intl('Accept Invitation →')}
            </BtnPrimary>
            <footer class="pt-2 border-t">
              {forgotSent ? (
                <p class="text-green-700 text-sm">{intl('We emailed you a link to log in.')}</p>
              ) : (
                <Button
                  class="text-indigo-600 text-sm"
                  onClick={async () => {
                    try {
                      await rpx.auth.forgotPassword({
                        email: invitation.email,
                        returnURL: location.pathname + location.search,
                      });
                      setForgotSent(true);
                    } catch (err) {
                      showError(err);
                    }
                  }}
                >
                  {intl('Forgot your password?')}
                </Button>
              )}
            </footer>
          </form>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div class="flex flex-col gap-6 items-center text-center">
        <CourseBanner course={course} />
        <div class="flex flex-col gap-3">
          <h2 class="text-2xl font-bold">{`Welcome to ${course.title}!`}</h2>
          <p class="text-lg text-gray-600">
            {`Hi ${firstName}, we're really excited to have you join our community.`}
          </p>
        </div>
        {acceptError && (
          <div class="w-full max-w-sm bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
            {intl('Something went wrong. Please try again.')}
          </div>
        )}
        <BtnPrimary
          class="w-full max-w-sm py-3 rounded-md font-semibold text-lg"
          isLoading={isLoading}
          onClick={handleAccept}
        >
          {acceptError ? intl('Try Again →') : intl('Accept Invitation & Join →')}
        </BtnPrimary>
      </div>
    </PageWrapper>
  );
}

function PageWrapper({ children }: { children: ComponentChildren }) {
  const tenant = useCurrentTenant();
  return (
    <div class="min-h-screen">
      <div class="w-full max-w-lg mx-auto px-6 py-12 flex flex-col items-center gap-8">
        {tenant.isCore ? <MiraseeLogo /> : <SiteNavLogo />}
        {children}
      </div>
    </div>
  );
}

function ProfilePhotoUpload({ user }: { user: NonNullable<ReturnType<typeof useCurrentUser>> }) {
  const auth = useAuth();
  const imageUrl = useImageUrl(user.profilePhotoUrl, { maxWidth: 256 });

  const handleUpload = async () => {
    try {
      const result = await filepicker({
        sources: ['filepicker', 'takephoto'],
        requireCrop: true,
        cropRatio: 1,
        accept: 'image/*',
      });
      if (!result?.fileId || !result?.filePath) {
        return;
      }
      await rpx.auth.updateCurrentUserProfilePhoto({
        profilePhotoUrl: result.filePath,
      });
      auth.setUser({ ...user, profilePhotoUrl: result.filePath });
    } catch {
      showError(new Error(intl('There was a problem uploading your photo.')));
    }
  };

  if (imageUrl) {
    return (
      <div class="flex flex-col items-center gap-2">
        <Button type="button" class="relative rounded-full" onClick={handleUpload}>
          <UserProfileIcon user={user} size="size-24" />
          <span class="absolute bottom-0 right-0 bg-white rounded-full p-1.5 shadow-md border border-gray-200">
            <svg
              class="w-3.5 h-3.5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </span>
        </Button>
        <span class="text-sm text-green-600 font-medium">{intl('Photo added!')}</span>
      </div>
    );
  }

  return (
    <Button type="button" class="flex flex-col items-center gap-2 group" onClick={handleUpload}>
      <span class="size-24 rounded-full border-2 border-dashed border-gray-300 group-hover:border-indigo-400 flex items-center justify-center bg-gray-50 group-hover:bg-indigo-50 transition">
        <svg
          class="w-8 h-8 text-gray-400 group-hover:text-indigo-500 transition"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
          />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </span>
      <span class="text-sm text-indigo-600 font-medium group-hover:text-indigo-700">
        {intl('Add your photo')}
      </span>
    </Button>
  );
}

function ProfileStep({ onContinue, onSkip }: { onContinue: () => void; onSkip: () => void }) {
  const user = useCurrentUser()!;
  const [bio, setBio] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleContinue = async () => {
    try {
      setIsSaving(true);
      if (bio.trim()) {
        await rpx.auth.updateUser({
          id: user.id,
          name: user.name,
          email: user.email!,
          timezone: user.timezone,
          bio,
        });
      }
      onContinue();
    } catch (err) {
      showError(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div class="flex flex-col gap-8 items-center w-full">
      <div class="flex flex-col gap-2 text-center">
        <h2 class="text-2xl font-bold">{intl('Introduce yourself!')}</h2>
        <p class="text-gray-600">
          {intl(
            'This is a community — people want to know who you are. A photo and a few words go a long way.',
          )}
        </p>
      </div>
      <ProfilePhotoUpload user={user} />
      <div class="w-full flex flex-col gap-2">
        <label class="text-sm font-semibold text-gray-700">{intl('About you')}</label>
        <BioEditor
          value={bio}
          onChange={setBio}
          class="rounded-lg"
          placeholder={intl('What do you do? What are you excited to learn?')}
        />
      </div>
      <div class="flex flex-col gap-3 w-full max-w-sm">
        <BtnPrimary
          class="w-full py-3 rounded-md font-semibold"
          isLoading={isSaving}
          onClick={handleContinue}
        >
          {intl('Save & Continue →')}
        </BtnPrimary>
        <Button class="text-gray-400 hover:text-gray-500 text-xs" onClick={onSkip}>
          {intl('skip')}
        </Button>
      </div>
    </div>
  );
}

function LegacyInvitationFlow({ props }: { props: Props }) {
  const user = useCurrentUser();
  const { course, invitationString, invitation } = props.state;
  const actionText = intl('Accept Invitation →');

  const acceptInvitation = async () => {
    await rpx.invitations.acceptInvitation({
      courseId: course.id,
      invitation: invitationString,
    });
    if (course.id === AIP_COURSE_ID) {
      props.router.redirectTo('/community');
    } else {
      await redirectToCourse(props.router, course);
    }
  };

  return (
    <CourseCheckoutPage
      course={course}
      title={course.isBundle ? intl('Join courses') : intl('Join course')}
      showHeader={false}
      subtitle={
        course.isBundle
          ? intl(`You've been invited to join these courses!`)
          : intl(`You've been invited to join this course!`)
      }
    >
      {!user && (
        <AuthForm
          title={intl('Accept invitation')}
          subtitle={intl('To accept this invitation, enter your email address.')}
          initialEmail={invitation.email}
          finalActionText={actionText}
          onSubmit={async () => {
            props.router.goto(location.pathname + setUrlSearchParam('auto', 'true').toString());
          }}
        />
      )}
      {!!user && (
        <CheckoutForm
          autoSubmit={props.params.auto === 'true'}
          actionText={actionText}
          onSubmit={acceptInvitation}
        ></CheckoutForm>
      )}
    </CourseCheckoutPage>
  );
}

// Consent copy intentionally hard-codes the AITM/AIP/Aug 28 references for
// this one-off cohort. When generalizing to a future cohort, drive the date
// from server-side AITM_AIP_TRIAL_END (in server/rpx/endpoints/invitations.ts)
// and parameterize the course/source names via getInvitationState.
function TrialConsent({
  firstName,
  isLoading,
  acceptError,
  onAccept,
}: {
  firstName: string;
  isLoading: boolean;
  acceptError: boolean;
  onAccept: () => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <div class="flex flex-col gap-6 w-full">
      <div class="flex flex-col gap-3">
        <h2 class="text-2xl font-bold">{intl(`Welcome to AI Playground`)}</h2>
        <p class="text-gray-700">
          {`Hi ${firstName}, you've been invited to join AI Playground as part of your AI Time Machine experience. Your trial runs through August 28, with full access to all live courses, workshops, trainings, and the community.`}
        </p>
      </div>
      <div class="flex flex-col gap-3">
        <h3 class="font-semibold">{intl(`Here's what happens next:`)}</h3>
        <ul class="flex flex-col gap-2 text-gray-700">
          <li class="pl-4 border-l-2 border-indigo-500">
            {intl(`You have full AIP access starting today through August 28 — no charges`)}
          </li>
          <li class="pl-4 border-l-2 border-indigo-500">
            {intl(
              `On August 28, you'll see your first monthly payment of $97 on the card you used for AI Time Machine`,
            )}
          </li>
          <li class="pl-4 border-l-2 border-indigo-500">
            {intl(`Going forward, you'll be billed $97/month — you don't have to lift a finger`)}
          </li>
          <li class="pl-4 border-l-2 border-indigo-500">
            {intl(`You may cancel anytime before August 28 and you won't be charged a thing`)}
          </li>
        </ul>
      </div>
      <label class="flex gap-3 items-start bg-amber-50 border border-amber-200 rounded p-4 cursor-pointer">
        <input
          type="checkbox"
          class="mt-1"
          checked={acknowledged}
          onChange={(e) => setAcknowledged((e.target as HTMLInputElement).checked)}
        />
        <span class="text-sm text-gray-800">
          {intl(
            `I understand my card will be charged $97/month starting August 28 unless I cancel.`,
          )}
        </span>
      </label>
      {acceptError && (
        <div class="w-full bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {intl(`Something went wrong. Please try again, or reach out to support@mirasee.com.`)}
        </div>
      )}
      <BtnPrimary
        class="w-full py-3 rounded-md font-semibold"
        isLoading={isLoading}
        disabled={!acknowledged}
        onClick={onAccept}
      >
        {intl(`Start my AI Playground trial`)}
      </BtnPrimary>
      <p class="text-xs text-gray-500 text-center">
        {intl(`Cancel anytime from your account billing page.`)}
      </p>
    </div>
  );
}
